import { ProductDto, CreateProductDto, PatchProductDto, ProductPaginationResponse } from "../interfaces/ProductInterfaces";
import { parseApiErrorResponse } from "../utils/parseApiError";

class ProductApi {
  // Create a new product
  static async createProduct(productData: CreateProductDto): Promise<ProductDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/product/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(productData),
    });

    if (response.ok) {
      const product: ProductDto = await response.json();
      return product;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create product' }));
      throw new Error(errorData.message || errorData.error || 'Failed to create product');
    }
  }

  // Get all products with pagination and search
  static async getProducts(params: {
    from?: number;
    to?: number;
    search?: string;
  } = {}): Promise<ProductPaginationResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.from !== undefined) queryParams.append('from', params.from.toString());
    if (params.to !== undefined) queryParams.append('to', params.to.toString());
    if (params.search) queryParams.append('search', params.search);

    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/product/list?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const result: ProductPaginationResponse = await response.json();
      return result;
    } else {
      const message = await parseApiErrorResponse(response, 'Failed to fetch products');
      throw new Error(message);
    }
  }

  // Get a specific product by GUID
  static async getProduct(productGuid: string): Promise<ProductDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/product/${productGuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const product: ProductDto = await response.json();
      return product;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch product' }));
      throw new Error(errorData.message || errorData.error || 'Failed to fetch product');
    }
  }

  // Update an existing product
  static async updateProduct(productGuid: string, updateData: PatchProductDto): Promise<ProductDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/product/${productGuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(updateData),
    });

    if (response.ok) {
      const product: ProductDto = await response.json();
      return product;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update product' }));
      throw new Error(errorData.message || errorData.error || 'Failed to update product');
    }
  }

  // Delete a product
  static async deleteProduct(productGuid: string): Promise<{ message: string; data: ProductDto }> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/product/${productGuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const result: { message: string; data: ProductDto } = await response.json();
      return result;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete product' }));
      throw new Error(errorData.message || errorData.error || 'Failed to delete product');
    }
  }
}

export default ProductApi;